import QueryToJson from './QueryToJson'
import JsonToQuery from './JsonToQuery'

class ItemsSearchUrlBuilder {
  constructor() {
    this.pathname = null
    this.search = null
    this.extraSource = null
    this.productType = 'cg'
    this.dedicatedPathnames = I18n.t('item-search-filters.dedicated-search-page-url')
    this.print_ready = I18n.t('item-search-filters.3d-print-models-url')

    this.dedicateFileTypeFragments = {}
  }

  set(options) {
    this.pathname = options.pathname
    this.search = options.search
  }

  setExtraSource(extraSource) {
    this.extraSource = extraSource
  }

  setDedicateFileTypeFragments(pathnames) {
    this.dedicateFileTypeFragments = pathnames
  }

  setProductType(productType) {
    this.productType = productType
  }

  parseParams() {
    let filter
    let pathFragment
    const ref = this.dedicatedPathnames
    const params = QueryToJson(this.search.replace(/^\?/, ''))

    if (this.pathname.includes('/ext/')) {
      params.file_types = this.extraSource.file_types
    }

    for (filter in ref) {
      pathFragment = ref[filter]
      if (this.pathname.includes(pathFragment)) {
        params[filter] = '1'
      }
    }

    if (this.pathname.includes(I18n.t('item-search-filters.3d-print-models-url'))) {
      params.print_ready = '1'
    }

    if (!Object.keys(Object(params.keywords)).length) {
      delete params.keywords
    }

    delete params['']

    return params
  }

  applyFilter(key, value) {
    const newParams = this.parseParams()

    if (key === 'file_types') {
      if (newParams.file_types == null) {
        newParams.file_types = []
      }
    }

    if (key === 'licenses') {
      if (newParams.licenses == null) {
        newParams.licenses = []
      }
    }

    if (Array.isArray(newParams[key])) {
      if (!newParams[key].includes(value)) {
        newParams[key].push(value)
      }
    } else {
      newParams[key] = value
    }

    if (newParams.free === '1') {
      delete newParams.price
    }

    if (key !== 'page') {
      delete newParams.page
    }

    return this.adjustParamsAndBuildLocation(this.pathname, newParams)
  }

  removeFilter(key, subValue) {
    let newPathname = this.pathname
    const newParams = this.parseParams()

    delete newParams.page

    if ((subValue != null) && Array.isArray(newParams[key])) {
      newParams[key] = newParams[key].filter(value => `${value}` !== `${subValue}`)
      if (!Object.keys(newParams[key]).length) {
        delete newParams[key]
        newPathname = this.removeDedicatedFileTypeFromPathname(newPathname)
      }
    } else if (key === 'keywords') {
      if (newParams[key]) {
        newParams[key] = newParams[key].replace(new RegExp(`\\b${subValue}\\b`, 'gi'), '').replace(/(^\s*)|(\s*$)/g, '')
        if (!Object.keys(newParams[key]).length) {
          delete newParams[key]
        }
      }
    } else if (newParams[key] != null) {
      delete newParams[key]
    } else if (key === 'tag' || key === 'parent_category') {
      newPathname = newPathname.split('/').slice(0, 2).join('/')
    } else if (key === 'subcategory') {
      newPathname = newPathname.split('/').slice(0, 3).join('/')
    }

    return this.adjustParamsAndBuildLocation(newPathname, newParams)
  }

  adjustParamsAndBuildLocation(pathname, newParams) {
    [pathname, newParams] = this.adjustDedicatedFilters(pathname, newParams)

    return this.buildLocation(pathname, newParams)
  }

  build() {
    return this.adjustParamsAndBuildLocation(this.pathname, this.parseParams())
  }

  adjustDedicatedFilters(pathname, params) {
    let useLocaleInUrl = I18n.locale !== I18n.defaultLocale
    let filter
    let pathFragment
    const ref = this.dedicatedPathnames
    const singleDedicatedFilter = this.findDedicatedFilter(params)
    const dedicatedFileType = this.findDedicatedFileType(params)
    const isCategorySearch = this.isTagOrCategoryPath(pathname)
        && !pathname.includes('/ext/')

    // Remove every dedicated pathname fragment
    pathname = this.removeDedicatedFileTypeFromPathname(pathname)

    if (params.print_ready && !pathname.includes(this.print_ready)) {
      this.setProductType('printable')

      if (isCategorySearch) {
        pathname = `${this.locationByProductType()}/${this.getTagOrCategory(pathname)}`
      } else {
        pathname = this.locationByProductType()
      }
    } else if (!params.print_ready && pathname.includes(this.print_ready)) {
      // removing printable filter

      this.setProductType('cg')

      // when we're removing product type filter in category or tag search,
      if (isCategorySearch) {
        pathname = `${this.locationByProductType()}/${this.getTagOrCategory(pathname)}`
      } else {
        pathname = this.locationByProductType()
      }
    }

    for (filter in ref) {
      pathFragment = ref[filter]

      if (pathname.includes(pathFragment)) {
        pathname = pathname.replace(pathFragment, '/')
      }
    }

    if (params.print_ready) {
      delete params.polygons
      delete params.collection
      delete params.print_ready
      delete params.pbr
    }

    if (singleDedicatedFilter != null) {
      pathFragment = this.dedicatedPathnames[singleDedicatedFilter]

      if (params[singleDedicatedFilter] && !pathname.includes(pathFragment)) {
        if (!(singleDedicatedFilter !== 'free' && pathname.includes(this.print_ready))) {
          let normalizedLocale = I18n.locale.toLowerCase()
          let localeFriendlyRegexp = new RegExp(`^(\\/${normalizedLocale}\\/|\\/)`)
          let localeFriendlyFragment = useLocaleInUrl ? `/${normalizedLocale}${pathFragment}` : `${pathFragment}`

          pathname = pathname.replace(localeFriendlyRegexp, localeFriendlyFragment)
        }
      }
      delete params[singleDedicatedFilter]
    } else if ((dedicatedFileType != null) && !this.isTagOrCategoryPath(pathname)) {
      pathname = `${this.locationByProductType()}/ext/${dedicatedFileType}`
      delete params.file_types
    }

    return [pathname, params]
  }

  findDedicatedFilter(params) {
    const activeDedicatedFilters = Object.keys(this.dedicatedPathnames)
      .filter(word => Object.keys(params).indexOf(word) > -1 )

    if (activeDedicatedFilters.includes('free')) {
      // Free dedicated path has priority
      return 'free'
    }
    if (activeDedicatedFilters.length === 1) {
      return activeDedicatedFilters[0]
    }
  }

  findDedicatedFileType(params) {
    const ref = params.file_types

    if ((ref != null ? ref.length : void 0) === 1) {
      return this.dedicateFileTypeFragments[params.file_types[0]]
    }
  }

  buildLocation(location, params) {
    return Object.keys(params).length === 0 ? location : `${location}?${JsonToQuery(params)}`
  }

  removeDedicatedFileTypeFromPathname(pathname) {
    const pathPartsToTake = this.pathnameStartsWithLocale(pathname) ? 3 : 2

    if (pathname.includes('/ext/')) {
      return pathname.split('/').slice(0, pathPartsToTake).join('/')
    } else {
      return pathname
    }
  }

  isTagOrCategoryPath(pathname) {
    const tagOrCategoryPathPartsSize = this.pathnameStartsWithLocale(pathname) ? 3 : 2

    return pathname.split('/').length > tagOrCategoryPathPartsSize
  }

  getTagOrCategory(pathname) {
    const tagOrCategoryPathPartsSize = this.pathnameStartsWithLocale(pathname) ? 3 : 2
    const pathEntries = pathname.split('/')

    return pathEntries[tagOrCategoryPathPartsSize]
  }

  locationByProductType() {
    let normalizedLocale = I18n.locale.toLowerCase()
    let useLocaleInUrl = I18n.locale !== I18n.defaultLocale
    let translatedLocationKey = (this.productType === 'printable' ?
      'item-search-filters.3d-print-models-url' :
      'item-search-filters.3d-models-url'
    )

    let translatedLocationUrl = (useLocaleInUrl ?
      `/${normalizedLocale}/${I18n.t(translatedLocationKey)}` :
      `/${I18n.t(translatedLocationKey)}`
    )

    return translatedLocationUrl
  }

  pathnameStartsWithLocale(pathname) {
    return pathname.startsWith(`/${I18n.locale.toLowerCase()}`)
  }
}

export default ItemsSearchUrlBuilder
